import moment, {Moment} from 'moment-timezone';
import * as gql from 'gql-query-builder';
import IQueryBuilderOptions from "gql-query-builder/build/IQueryBuilderOptions";
import Fields from "gql-query-builder/build/Fields";
import VariableOptions from "gql-query-builder/build/VariableOptions";
import {ClassTransformer, Transform, Type} from "class-transformer";
import {forkJoin, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {GRAPHQL_TYPES, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {BaseService} from '@shared/services/api/base-service';
import {PagedModel} from '@shared/api/model/paged-model';
import {PageInfo} from '@shared/components/ace-table/ace-table.component';
import {Collector, CollectorType, Device, DeviceObject, Equipment, EquipmentId, Favourite, History, Level, Long, Maybe, Metadata, Zone} from '@app/graphql-typings/graphql';
import {HistoryImpl} from '@shared/services/api/history-entity';
import {FavouriteSearchTerm} from './favourites.types';
import {MetadataImpl} from '@shared/services/api/metadata.service';
import {CollectorImpl} from '@shared/services/api/collectors.service';
import {DeviceObjectImpl} from '../bacers/bacer-devices/bacer-devices.service';
import {EquipmentService, EquipmentScopeOptions} from '@shared/services/api/equipment.service';
import {CommissionDataNavigationState} from "@app/layout/sitemanager/types/types";
import {RoutingStateService} from "@shared/services/routing/routing-state.service";
import {SiteService} from "@shared/services";
import {SiteInfo} from "@shared/services/api/access-sites.interface";
import {NavigationCacheService} from "@app/layout/sitemanager/services/navigation-cache.service";
import {objectToHttpParams} from '@app/shared/util/angular/angular-utils';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {ObjectUtils} from '@shared/util/general/object-utils';
import {StringUtils} from '@shared/util/general/string-utils';

export class FavouriteScopeOptions {
  getLatestHistory?: boolean;
  getDeviceObject?: boolean;
  getEquipment?: boolean;
  getEquipmentCollector?: boolean;
}

export interface FavouriteDeletedResponse {
  deleted: boolean;
  fav_id: number;
}

export interface FavouriteResponse {
  favourite?: Favourite | null;
  equipment: Equipment | null;
  device?: Device | null;
  deviceObjects?: DeviceObject[] | null;
  site: SiteInfo;
  collector: Collector;
  levels?: Level[];
}

interface HistorySearchTerm {
  fav_id?:number;
  fav_ids?: number[];
  start?: Moment;
  end?: Moment;
  latest?: boolean;
  end_exclusive?: boolean;
}
interface FavouriteCommissioningValidation extends Favourite {
  device_id?: Long;
  bacnet_device_id?: number;
  equipment_name?: string;
}

export enum FavouritesAggregationEntity {
  SITE = 'site',
  METADATA = 'metadata',
  METADATA_TYPE = 'metadata_type',
  EQUIPMENT = 'equipment',
  ALL = 'all'
}

export interface FavouritesAggregationParams {
  metadata_id?: number;
  metadata_ids?: number[];
  metadata_type_id?: number;
  metadata_type_ids?: number[];
  site_id?: number;
  site_ids?: number[];
  equipment_id?: number;
  equipment_ids?: number[];
  aggregate_entity: FavouritesAggregationEntity;
  limit?: number;
  start_index?: number;
}

export interface FavouriteCountAggregation {
  site_id?: number;
  equipment_id?: number;
  metadata_id?: number;
  metadata_type_id?: number;
  count: number;
}

export interface FavouritesAggregationResponse {
  favourite_count_aggregation: FavouriteCountAggregation[];
}


export class FavouriteImpl implements Favourite {
  canonical_equipment_id: Long;
  comment?: string;
  equipment?: Equipment;
  equipment_id?: Long;
  equipment_ids?: Long[];
  fav_id?: Long;
  identifier?: string;
  device_object_id?: Long;
  @Type(() => DeviceObjectImpl)
  device_object?: DeviceObject;
  is_active?: boolean;
  @Type(() => MetadataImpl)
  metadata?: Metadata;
  metadata_id?: number;
  @Type( () => CollectorImpl)
  collector?: Collector;
  zone_id?: number;
  @Type( () => HistoryImpl)
  history?: History[];
  zone?: Maybe<Zone>;
  @Type(() => moment)
  @Transform(({value}: { value: string | null }) => value ? moment.parseZone(value) : value, {toClassOnly: true})
  @Transform(({value}: { value: Moment | null }) => value ? value.toJSON() : value, {toPlainOnly: true})
  last_modified_at?: Moment;
}


/** Days of history one `getFavouriteHistory` call can cover, shared out across the favourites requested.
 *
 * We need to ensure the payloads are less than 10MB in size, as that's the AWS API Gateway's limit, and need to ensure the
 * requests take less than 30 seconds to complete, as otherwise they are timed out by the API gateway.
 * 1 favourite's history for 1000 days is approximately 4.5 MB, so we can request up to 2000 days of history for a single
 * favourite (9MB) with respect to size. However, this causes a timeout, as the request take longer than 30 seconds.
 * An investigation suggests that 300 days is a number that minimises timeouts. */
export const FAVOURITE_HISTORY_REQUEST_DAYS = 300;

export const favouriteResolver: ResolveFn<FavouriteResponse> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<FavouriteResponse> => {
  const navigationState: CommissionDataNavigationState = inject(RoutingStateService).getNavigationState<CommissionDataNavigationState>();
  const favouritesService: FavouritesService = inject(FavouritesService);
  const sitesService: SiteService = inject(SiteService);
  const navigationCacheService: NavigationCacheService = inject(NavigationCacheService);
  const equipmentService: EquipmentService = inject(EquipmentService);
  const favouriteId: number = StringUtils.stringToNumber(route.paramMap.get('id'));
  const siteId: number = StringUtils.stringToNumber(route.paramMap.get('site-id'));
  const equipmentId: number = StringUtils.stringToNumber(route.paramMap.get('equipment-id'));
  const collectorType: CollectorType = route.routeConfig.data.collector_type;
  const favouriteScopeOptions: FavouriteScopeOptions = {getLatestHistory: true, getDeviceObject: true, getEquipment: true};
  const equipmentSearchScopeOptions: EquipmentScopeOptions = {
    getDeviceData: collectorType === CollectorType.Physical
  };
  const site$: Observable<SiteInfo> = sitesService.getCachedSiteInfo(siteId);
  const collector$: Observable<Collector> = navigationState?.collector
    ? of(navigationState.collector)
    : navigationCacheService.getSiteCollector(siteId, collectorType);
  const levels$: Observable<Level[]> = navigationState?.levels
    ? of(navigationState.levels)
    : navigationCacheService.getSiteLevels(siteId);
  const equipment$: Observable<Equipment> = navigationState?.equipment
    ? of(navigationState?.equipment.find((eq: Equipment) => eq.equipment_id === equipmentId))
    : equipmentService.getEquipmentById(equipmentId, equipmentSearchScopeOptions);
  const favourite$: Observable<Favourite> = favouriteId != null
    ? favouritesService.getFavouriteById(favouriteId, favouriteScopeOptions)
    : of(null);

  return forkJoin([site$, collector$, levels$, favourite$, equipment$])
    .pipe(map( ([site, collector, levels, favourite, equipment]: [SiteInfo, Collector, Level[], Favourite, Equipment]) => {
      return { site, levels, favourite, equipment, collector };
    }));
};

@Injectable()
export class FavouritesService extends BaseService {
  public static readonly modelMapping = {Favourite: FavouriteImpl, History: HistoryImpl};
  private readonly logger: AceLogger;
  private headers: HttpHeaders = this.getHeaders();
  private readonly FETCH_PAGE_SIZE = 500;
  // On average there are about 10 favourites per equipment, but just to be safe (as paging is expensive),
  // assume 16 per equipment
  private readonly FAVOURITES_FOR_EQUIPMENT_PARTITION_SIZE = Math.floor(this.FETCH_PAGE_SIZE / 16);
  private readonly FAVOURITE_KEY = 'favourite';
  private readonly FAVOURITE_KEY_PLURAL = this.FAVOURITE_KEY+'s';
  private readonly FAVOURITES_GRAPHQL_OPERATION = this.FAVOURITE_KEY_PLURAL;
  private readonly HISTORY_GRAPHQL_OPERATION = 'history';
  private readonly FAVOURITE_GRAPHQL_FIELDS: Fields = [
    'fav_id',
    'equipment_id',
    'equipment_ids',
    'canonical_equipment_id',
    'metadata_id',
    'device_object_id',
    'identifier',
    'is_active',
    'zone_id',
    'comment',
    'last_modified_at',
    {
      zone: [
        'zone_id', 'system',
        { zone_name: ['zone_name_id', 'zone_name', 'system'] },
        { level: ['level_id', 'level_name', 'system'] }
      ]
    },
    {
      metadata: [
        'metadata_id', 'name', 'code','unit_id', 'customer_id', 'shared', 'type_id',
        { unit: ['unit', 'unit_id', 'shared'] },
        { type: ['type_id', 'type', 'type_code', 'shared'] }
      ]
    }
  ];

  private readonly HISTORY_GRAPHQL_FIELDS: Fields = ['fav_id', 'data', 'ts'];

  private readonly FAVOURITES_VARIABLES: VariableOptions = {
    site_id: { type: GRAPHQL_TYPES.INTEGER },
    fav_id: { type: GRAPHQL_TYPES.LONG },
    fav_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    equipment_id: { type: GRAPHQL_TYPES.LONG },
    canonical_equipment_id: { type: GRAPHQL_TYPES.LONG },
    equipment_ids: { type: GRAPHQL_TYPES.LONG, list: true },
    metadata_name: { type: GRAPHQL_TYPES.STRING },
    metadata_id: { type: GRAPHQL_TYPES.INTEGER },
    metadata_ids: { type: GRAPHQL_TYPES.INTEGER, list: true },
    zone_id: { type: GRAPHQL_TYPES.LONG },
    device_id: { type: GRAPHQL_TYPES.LONG },
    identifier: { type: GRAPHQL_TYPES.STRING },
    identifier_filter: { type: GRAPHQL_TYPES.STRING },
    metadata_codes: {type: GRAPHQL_TYPES.STRING, list: true },
    zone_name_id: { type: GRAPHQL_TYPES.LONG },
    level_id: { type: GRAPHQL_TYPES.LONG },
    favourite_type: { type: 'CollectorConfigurationTypeEnum' },
    system: { type: GRAPHQL_TYPES.BOOLEAN },
    commissionable: { type: GRAPHQL_TYPES.BOOLEAN },
    start_index: { type: GRAPHQL_TYPES.INTEGER, required: true },
    limit: { type: GRAPHQL_TYPES.INTEGER, required: true }
  };

  private readonly HISTORY_VARIABLES: VariableOptions = {
    fav_id: {type: GRAPHQL_TYPES.LONG},
    fav_ids: {type: GRAPHQL_TYPES.LONG, list: true},
    start: {type: GRAPHQL_TYPES.DATE_TIME},
    end : {type: GRAPHQL_TYPES.DATE_TIME},
    latest: {type: GRAPHQL_TYPES.BOOLEAN},
    end_exclusive: {type: GRAPHQL_TYPES.BOOLEAN}
  };

  private readonly FAVOURITE_GRAPHQL_EQUIPMENT_FIELDS: Fields = [
    {equipment: ['equipment_id', 'name', 'collector_id', 'zone_id', 'shared', 'metadata_type_id', 'virtual']}];

  private readonly FAVOURITE_GRAPHQL_EQUIPMENT_COLLECTOR_FIELDS: Fields = [{
    collector: ['collector_id', 'hostname', {site: ['site_id', 'site_name']}]
  }];

  private readonly FAVOURITE_GRAPHQL_DEVICE_OBJECT_FIELDS: Fields = [
    {device_object: ['device_object_id', 'object_identifier', 'object_name', 'present_value', 'last_modified_at', 'units', 'device_id',
        {device: ['device_id', 'collector_id', 'bacnet_device_id', 'bacnet_device_name', 'device_id', 'device_label', 'lastseen_at', 'vendor', 'model']}]}];

  private readonly FAVOURITE_GET_LATEST_HISTORY_SUB_QUERY: IQueryBuilderOptions = {
    operation: 'history',
    fields: ['data', 'ts'],
    variables: {
      latest: {type: GRAPHQL_TYPES.BOOLEAN, value: true}
    },
  };

  zoneTempCodes = new Set(['ACB-Zn-T', 'PCHB-Zn-T', 'FCU-Zn-T', 'VAV-Zn-T', 'VRV-Zn-T', 'Un-Zn-T', 'PAC-Zn-T', 'ZnT']);
  zoneTempSetpointCodes = new Set(['ACB-Zn-TSP', 'PCHB-Zn-TSP', 'FCU-Zn-TSP', 'VAV-Zn-TSP', 'VRV-Zn-TSP', 'Un-Zn-TSP', 'PAC-Zn-TSP', 'ZnTSP']);
  zoneHumidityCodes = new Set(['ZnRH', 'VAV-Zn-RH', 'PCHB-Zn-RH', 'PAC-Zn-RH', 'FCU-Zn-RH', 'Un-Zn-RH', 'ACB-ZnRH']);
  zoneHumiditySetpointCodes = new Set(['ZnRHSP', 'VAV-Zn-RHSP', 'PCHB-Zn-RHSP', 'PAC-Zn-RHSP', 'FCU-Zn-RHSP', 'Un-Zn-RHSP', 'ACB-ZnRHSP']);
  allZoneMetadataCodes = new Set([...this.zoneTempCodes, ...this.zoneHumidityCodes, ...this.zoneTempSetpointCodes, ...this.zoneHumiditySetpointCodes ]);
  FAVOURITES_PAGE_LIMIT = 1000;
  levels: Level[] = [];

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private classTransformer: ClassTransformer,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(FavouritesService.name);
  }

  // Create or Update an array of favourites
  public upsertFavourite(favourites: Favourite[]): Observable<Favourite[]> {
    const options = {headers: this.headers};
    const wrapper = (favs: Favourite[]): Observable<Favourite[]> => {
      return this.http.post(this.getGateway() + '/favourites', favs, options)
        .pipe(map((response: APIResponse<Favourite[]>) => this.httpUtilityService.extractResponseData(response, this.FAVOURITE_KEY_PLURAL)));
    };
    return this.httpUtilityService.requestPartitioned(wrapper, favourites, this.FAVOURITES_PAGE_LIMIT);
  }

  public getFavouritesForEquipment(equipmentIds: EquipmentId[], scopeOptions?: FavouriteScopeOptions): Observable<Favourite[]> {
    if (equipmentIds?.length) {
      const partitionWrapper = (partitionedEquipmentIds: EquipmentId[]) =>
        this.searchAllFavourites({equipment_ids: partitionedEquipmentIds}, scopeOptions);
      return this.httpUtilityService.requestPartitioned(partitionWrapper, equipmentIds, this.FAVOURITES_FOR_EQUIPMENT_PARTITION_SIZE);
    } else {
      return of([]);
    }
  }

  public searchAllFavourites(searchTerm?: FavouriteSearchTerm, scopeOptions?: FavouriteScopeOptions): Observable<Favourite[]> {
    const favouritesSearchPageWrapper = (pageInfo): Observable<PagedModel<Favourite[]>> => {
      return this.searchFavourites(pageInfo, searchTerm, scopeOptions);
    };
    return this.httpUtilityService.pageUntilComplete(favouritesSearchPageWrapper, {first: 0, rows: this.FETCH_PAGE_SIZE});
  }

  public getFavouriteById(favId: number, scopeOptions?: FavouriteScopeOptions): Observable<Favourite> {
    return this.searchFavourites({first:0, rows: 1}, {fav_id: favId}, scopeOptions)
      .pipe(map(({data: favourites}: PagedModel<Favourite[]>) => CollectionUtils.only(favourites)));
  }

  public getFavouriteDetailsByFavIds(favIds: number[], scopeOptions?: FavouriteScopeOptions, progressCallback?: (a: number) => void): Observable<Favourite[]> {
    if (favIds?.length) {
      let completedSoFar = 0;
      const wrapper = (partitionedFavIds: number[]) => this.searchFavourites(
        {first:0, rows: Math.min(partitionedFavIds.length, this.FETCH_PAGE_SIZE)}, {fav_ids: partitionedFavIds}, scopeOptions)
        .pipe(map(({data: data}: PagedModel<Favourite[]>) => {
          if (typeof progressCallback === 'function') {
            completedSoFar += data.length;
            progressCallback(completedSoFar);
          }
          return data;
        }));
      return this.httpUtilityService.requestPartitioned(wrapper, favIds, this.FETCH_PAGE_SIZE);
    } else {
      return of([]);
    }
  }

  public getFavouritesAggregation(favouritesAggregationParams: FavouritesAggregationParams): Observable<FavouriteCountAggregation[]> {
    const endpoint = this.getGateway() + '/favourites/aggregation';
    return this.http.get<APIResponse<FavouritesAggregationResponse>>(endpoint, {params: objectToHttpParams(favouritesAggregationParams)})
      .pipe(map(response => response[this.DATA_PREFIX].favourite_count_aggregation));
  }

  public searchFavourites(pageInfo: PageInfo,
                          searchTerm: FavouriteSearchTerm,
                          scopeOptions?: FavouriteScopeOptions): Observable<PagedModel<Favourite[]>> {
    this.logger.debug('searchFavourites params', pageInfo, searchTerm, scopeOptions);
    const searchTermVariables = {...searchTerm,
      ...searchTerm.metadata_name && {metadata_name: '%' + searchTerm.metadata_name + '%'}};

    let equipmentFields: Fields = ObjectUtils.cloneDeep(this.FAVOURITE_GRAPHQL_EQUIPMENT_FIELDS);
    if (scopeOptions?.getEquipmentCollector) {
      equipmentFields[0]['equipment'].push(this.FAVOURITE_GRAPHQL_EQUIPMENT_COLLECTOR_FIELDS[0]);
    }

    let fields = [...this.FAVOURITE_GRAPHQL_FIELDS,
      ...scopeOptions?.getDeviceObject ? this.FAVOURITE_GRAPHQL_DEVICE_OBJECT_FIELDS : [],
      ...scopeOptions?.getEquipment || scopeOptions?.getEquipmentCollector ? equipmentFields : [],
      ...scopeOptions?.getLatestHistory ? [this.FAVOURITE_GET_LATEST_HISTORY_SUB_QUERY] : [],
    ];

    return this.getFavouriteDetailsPagedModel(fields, searchTermVariables, pageInfo).pipe(map((result: PagedModel<Favourite[]>) => {
      const favourites: Favourite[] = result.data;
      /*TODO - Remove delete identifier code when API no longer includes identifier in bacnet favourite responses */
      favourites.forEach((favourite: Favourite) => {
        if (favourite.device_object) {
          delete favourite.identifier;
        }
      });

      const returnData = {
        data: this.classTransformer.plainToInstance(FavouritesService.modelMapping['Favourite'], favourites),
        count: result.count
      };
      this.logger.debug('searchFavourites returnData', searchTerm, scopeOptions, returnData);
      return returnData;
    }));
  }

  private getFavouriteDetailsPagedModel(fields: Fields, criteria: FavouriteSearchTerm, pageInfo: PageInfo): Observable<PagedModel<Favourite[]>> {
    const gqlBuild = gql.query({
      operation: this.FAVOURITES_GRAPHQL_OPERATION,
      fields: fields,
      variables: this.FAVOURITES_VARIABLES
    });
    return this.httpUtilityService.graphQL<Favourite[]>(gqlBuild, this.FAVOURITES_GRAPHQL_OPERATION, {
      start_index: pageInfo.first,
      limit: pageInfo.rows || this.FETCH_PAGE_SIZE,
      ...criteria
    });
  }

  private getFavouritesWithFields(fields: Fields, criteria: FavouriteSearchTerm, pageSize = this.FETCH_PAGE_SIZE): Observable<Favourite[]> {
    return this.httpUtilityService.pageUntilComplete(pageInfo => this.getFavouriteDetailsPagedModel(fields, criteria, pageInfo), {first: 0, rows: pageSize});
  }

  public getFavouriteIdentifiers(criteria: FavouriteSearchTerm, pageSize = this.FETCH_PAGE_SIZE): Observable<Pick<Favourite, 'fav_id' | 'identifier'>[]> {
    return this.getFavouritesWithFields(['fav_id', 'identifier'], criteria, pageSize);
  }

  public getFavouriteHistory(favIds: number[], startTs: Moment, endTs: Moment): Observable<History[]> {
    this.logger.debug('getFavouriteHistory', favIds, startTs, endTs);
    if (favIds?.length) {
      const criteria: HistorySearchTerm = {fav_ids: favIds, start: startTs, end: endTs, end_exclusive: true};
      return this.searchHistory(criteria);
    } else {
      return of([]);
    }
  }

  public historyLatestValue(favId: number): Observable<History> {
    const criteria: HistorySearchTerm = {fav_id: favId, latest: true};
    return this.searchHistory(criteria).pipe(map((history: History[]) => CollectionUtils.only(history)));
  }

  private searchHistory(criteria: HistorySearchTerm): Observable<History[]> {
    const query = gql.query({
      operation: this.HISTORY_GRAPHQL_OPERATION,
      fields: this.HISTORY_GRAPHQL_FIELDS,
      variables: this.HISTORY_VARIABLES
    });
    return this.httpUtilityService.nonPagedGraphQL<History[]>(query, this.HISTORY_GRAPHQL_OPERATION, criteria)
      .pipe(map(history => this.classTransformer.plainToInstance(FavouritesService.modelMapping['History'], history)));
  }

  // Delete an favourite
  public deleteFavourite(favouriteId): Observable<FavouriteDeletedResponse> {
    const options = {headers: this.headers};
    return this.http.delete(this.getGateway() + '/favourites/' + favouriteId,  options)
      .pipe(map((response: APIResponse<FavouriteDeletedResponse>) => this.httpUtilityService.extractResponseData(response, this.FAVOURITE_KEY)));
  }


  public getMatchingFavouritesFromAPI(pageInfo: PageInfo, siteId: number, favourites: Favourite[]): Observable<PagedModel<Favourite[]>> {
    const criteria: any[] = [];
    favourites.forEach((favourite: Favourite) => {

      const zoneMetadataFavourite = this.allZoneMetadataCodes.has(favourite.metadata.code);
      criteria.push({
        equipment_id: favourite.equipment.equipment_id,
        device_id: favourite.device_object && favourite.device_object.device && favourite.device_object.device.device_id ? favourite.device_object.device.device_id : null,
        identifier: favourite.device_object ? favourite.device_object.object_identifier : favourite.identifier,
        metadata_id: favourite.metadata.metadata_id,
        zone_id: favourite.zone_id,
        ...zoneMetadataFavourite &&  {metadata_codes: Array.from(this.allZoneMetadataCodes)},
      });
    });
    const data = {'criteria': criteria, start_index: pageInfo.first, limit: pageInfo.rows};
    const options = {headers: this.headers};

    return this.http.post(this.getGateway() + '/sites/' + siteId + '/favourites/commissioning-validation', data, options).pipe(
      map((response: {data: {favourites: Favourite[]}}) => {
        const favouritesResult: Favourite[] = this.httpUtilityService.extractResponseData(response, this.FAVOURITE_KEY_PLURAL);
        const enrichedFavourites: Favourite[] = favouritesResult.map((favouriteFromAPI: FavouriteCommissioningValidation) => {
          this.logger.debug('getMatchingFavouritesFromAPI response', {...favouriteFromAPI});
          return {
            fav_id: favouriteFromAPI.fav_id,
            identifier: favouriteFromAPI.identifier,
            ...(favouriteFromAPI.device_object_id &&
              { device_object_id: favouriteFromAPI.device_object_id,
                device_object: {device_object_id: favouriteFromAPI.device_object_id,
                  object_identifier: favouriteFromAPI.identifier,
                  device: {bacnet_device_id: favouriteFromAPI.bacnet_device_id, device_id: favouriteFromAPI.device_id}} }),
            metadata: favouriteFromAPI.metadata,
            zone_id: favouriteFromAPI.zone_id,
            equipment: { equipment_id: favouriteFromAPI.equipment_id, name: favouriteFromAPI.equipment_name},
          };
        });
        return {count: null, data: enrichedFavourites};
      }));
  }

  public getAllMatchingFavouritesFromAPI(favouritesReadyForSubmission: Favourite[], siteId: number): Observable<Favourite[]> {
    this.logger.debug('getMatchingFavouritesFromAPI ', favouritesReadyForSubmission);
    const validateFavouritesPageWrapper = (favourites: Favourite[]): Observable<Favourite[]> => {
      // Use pageUntilComplete just in case the response is paged
      return this.httpUtilityService.pageUntilComplete((pageInfo: PageInfo): Observable<PagedModel<Favourite[]>> => {
        return this.getMatchingFavouritesFromAPI(pageInfo, siteId, favourites);
      }, {first: 0, rows: this.FAVOURITES_PAGE_LIMIT});
    };
    return this.httpUtilityService.requestPartitioned(validateFavouritesPageWrapper, favouritesReadyForSubmission, this.FAVOURITES_PAGE_LIMIT);
  }

  public addFavouritesToEquipment(equipment: Equipment[], favourites: Favourite[]): void {
    this.logger.debug('addFavouritesToEquipment', equipment, favourites);
    if (equipment?.length && favourites?.length) {
      const equipmentById = CollectionUtils.indexBy(equipment, 'equipment_id');
      favourites.forEach((favourite: Favourite) => {
        const targetEquipment = equipmentById.get(favourite.canonical_equipment_id);
        if (targetEquipment) {
          targetEquipment.canonical_favourite_ids = [...new Set([...targetEquipment.canonical_favourite_ids, favourite.fav_id])];
          if (targetEquipment.shared) {
            targetEquipment.related_equipment?.forEach((relatedEquipmentId: number) => {
              const relatedEquipment: Equipment = equipmentById.get(relatedEquipmentId);
              if (relatedEquipment) {
                relatedEquipment.favourite_ids = [...new Set([...relatedEquipment.favourite_ids, favourite.fav_id])];
              } else {
                throw new Error('Cannot find related equipment with id ' + relatedEquipmentId + ' for equipment with id ' + targetEquipment.equipment_id);
              }
            });
          } else {
            targetEquipment.favourite_ids = [...new Set([...targetEquipment.favourite_ids, favourite.fav_id])];
          }
        } else {
          throw new Error('Cannot find equipment with id ' + favourite.canonical_equipment_id);
        }
      });
    } else {
      throw new Error('Equipment and favourites must be provided');
    }
  }

  public removeFavouritesFromEquipment(equipment: Equipment[], favourites: Favourite[]): void {
    this.logger.debug('removeFavouritesFromEquipment', equipment, favourites);
    if (equipment?.length && favourites?.length) {
      const equipmentById = CollectionUtils.indexBy(equipment, 'equipment_id');
      favourites.forEach((favourite: Favourite) => {
        const targetEquipment = equipmentById.get(favourite.canonical_equipment_id);
        if (targetEquipment) {
          const canonicalFavIdIndex = targetEquipment.canonical_favourite_ids.findIndex((favId: number) => favId === favourite.fav_id);
          if (canonicalFavIdIndex >= 0) {
            targetEquipment.canonical_favourite_ids.splice(canonicalFavIdIndex, 1);
            if (targetEquipment.shared) {
              targetEquipment.related_equipment?.forEach((relatedEquipmentId: number) => {
                const relatedEquipment: Equipment = equipmentById.get(relatedEquipmentId);
                if (relatedEquipment) {
                  const favIdIndex = relatedEquipment.favourite_ids.findIndex((favId: number) => favId === favourite.fav_id);
                  if (favIdIndex >= 0) {
                    relatedEquipment.favourite_ids.splice(favIdIndex, 1);
                  }
                } else {
                  throw new Error('Cannot find related equipment with id ' + relatedEquipmentId);
                }
              });
            }
          }
          if (!targetEquipment.shared) {
            const favIdIndex = targetEquipment.favourite_ids.findIndex((favId: number) => favId === favourite.fav_id);
            if (favIdIndex >= 0) {
              targetEquipment.favourite_ids.splice(favIdIndex, 1);
            }
          }
        } else {
          throw new Error('Cannot find equipment with id ' + favourite.canonical_equipment_id);
        }
      });
    } else {
      throw new Error('Equipment and favourites must be provided');
    }
  }

}

